import type { StateKey, GameState } from '../types';
import { EventBus, Events } from './EventBus';

type StateCallback<T> = (value: T) => void;

class StateManagerClass {
  private state: Partial<GameState> = {};
  private subscribers = new Map<StateKey, Set<StateCallback<unknown>>>();

  set<K extends StateKey>(key: K, value: GameState[K]): void {
    const oldValue = this.state[key];
    this.state[key] = value;

    // Notify subscribers
    this.subscribers.get(key)?.forEach((cb) => cb(value));

    // Emit specific events for important changes
    if (key === 'playerData' && value) {
      EventBus.emit(Events.PLAYER_UPDATED, value);

      // Check for gold changes
      const oldGold = (oldValue as GameState['playerData'])?.gold;
      const newGold = (value as GameState['playerData'])?.gold;
      if (oldGold !== undefined && newGold !== oldGold) {
        EventBus.emit(Events.GOLD_CHANGED, { old: oldGold, new: newGold });
      }
    }

    if (key === 'combatData') {
      EventBus.emit(Events.COMBAT_STATE_CHANGED, value);
    }
  }

  get<K extends StateKey>(key: K): GameState[K] | undefined {
    return this.state[key] as GameState[K] | undefined;
  }

  subscribe<K extends StateKey>(
    key: K,
    callback: StateCallback<GameState[K] | undefined>
  ): () => void {
    if (!this.subscribers.has(key)) {
      this.subscribers.set(key, new Set());
    }
    this.subscribers.get(key)!.add(callback as StateCallback<unknown>);

    // Immediately call with current value
    const currentValue = this.state[key];
    if (currentValue !== undefined) {
      callback(currentValue as GameState[K]);
    }

    // Return unsubscribe function
    return () => {
      this.subscribers.get(key)?.delete(callback as StateCallback<unknown>);
    };
  }

  batchUpdate(data: Partial<GameState>): void {
    for (const [key, value] of Object.entries(data)) {
      if (value !== undefined) {
        this.set(key as StateKey, value);
      }
    }
  }

  clear(): void {
    this.state = {};
    this.subscribers.clear();
  }
}

export const StateManager = new StateManagerClass();
